import { useEffect, useState } from "react";
import { TrendingUp, ShoppingCart, Package, Clock, AlertTriangle } from "lucide-react";
import api, { naira } from "@/lib/api";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-sand border border-sandline rounded-xl p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-bark mb-2">
      <Icon className="w-4 h-4" />
      <span className="text-sm">{label}</span>
    </div>
    <p className="font-serif text-3xl text-espresso">{value}</p>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-bark" data-testid="dashboard-loading">Loading dashboard…</p>;

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif text-3xl text-espresso mb-1">Dashboard</h1>
      <p className="text-bark text-sm mb-8">Overview of your store performance.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={TrendingUp} label="Revenue" value={naira(data.revenue)} testid="stat-revenue" />
        <StatCard icon={ShoppingCart} label="Total orders" value={data.totalOrders} testid="stat-orders" />
        <StatCard icon={Clock} label="Pending" value={data.pendingOrders} testid="stat-pending" />
        <StatCard icon={Package} label="Products" value={data.productCount} testid="stat-products" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent orders */}
        <div className="border border-sandline rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-sandline bg-sand">
            <h2 className="font-medium text-espresso">Recent orders</h2>
          </div>
          <div className="divide-y divide-sandline">
            {data.recentOrders.length === 0 && <p className="p-5 text-bark text-sm">No orders yet.</p>}
            {data.recentOrders.map((o) => (
              <div key={o.id} className="px-5 py-3 flex justify-between items-center text-sm gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-espresso font-medium">#{o.orderNumber}</p>
                  <p className="text-bark truncate">{o.customer?.name}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-espresso">{naira(o.total)}</p>
                  <span className="text-xs text-bark">{o.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top products + low stock */}
        <div className="space-y-6">
          <div className="border border-sandline rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-sandline bg-sand">
              <h2 className="font-medium text-espresso">Top products</h2>
            </div>
            <div className="divide-y divide-sandline">
              {data.topProducts.length === 0 && <p className="p-5 text-bark text-sm">No sales yet.</p>}
              {data.topProducts.map((p) => (
                <div key={p.name} className="px-5 py-2.5 flex justify-between text-sm">
                  <span className="text-espresso">{p.name}</span>
                  <span className="text-bark">{p.qty} sold</span>
                </div>
              ))}
            </div>
          </div>

          {data.lowStock.length > 0 && (
            <div className="border border-terracotta/40 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-sandline bg-terracotta/10 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-terracotta" />
                <h2 className="font-medium text-espresso">Low stock</h2>
              </div>
              <div className="divide-y divide-sandline">
                {data.lowStock.map((p) => (
                  <div key={p.id} className="px-5 py-2.5 flex justify-between text-sm">
                    <span className="text-espresso">{p.name}</span>
                    <span className="text-terracotta font-medium">{p.stock} left</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
